import { BOARD_SIZE } from '../domain/Board';
import { Position } from '../domain/Position';
import { MovementValidator } from './MovementValidator';
import { EventBus } from '../events/EventBus';
import { MoveMadeEvent, TerritoryCapturedEvent, TrapTriggeredEvent, TrapResolvedEvent, SideEliminatedEvent, TerritoryBurnedEvent, CascadeCaptureEvent, SemiEncirclementEvent, } from '../events/GameEvents';
import { TerritoryGroupAnalyzer } from './capture/TerritoryGroupAnalyzer';
import { TrapResolver, TrapSituationType } from './trap/TrapResolver';
import { ReanimationReason } from './reanimation/ReanimationManager';
import { ForceCalculator } from './trap/ForceCalculator';
const TRAP_TYPE = {
    [TrapSituationType.ESCAPABLE]: 'escapable',
    [TrapSituationType.FORCE_BASED]: 'force',
    [TrapSituationType.SINGLE_COLOR]: 'monopoly',
};
/**
 * Move Executor
 * Исполняет команды движения и управляет побочными эффектами
 */
export class MoveExecutor {
    board;
    eventBus;
    trapResolver;
    syncPlayerChoiceHandler;
    validator;
    groupAnalyzer;
    moveHistory = [];
    pendingBurningLandAftermath = false;
    isProcessingBurningLandAftermath = false;
    // Who took each side's fields in the turn-end group conquest under way: the
    // side a group conquest knocks out went out on that side's capture.
    conquestTakers = new Map();
    constructor(board, eventBus, trapResolver, syncPlayerChoiceHandler) {
        this.board = board;
        this.eventBus = eventBus;
        this.trapResolver = trapResolver;
        this.syncPlayerChoiceHandler = syncPlayerChoiceHandler;
        this.validator = new MovementValidator(board);
        this.groupAnalyzer = new TerritoryGroupAnalyzer(board);
    }
    /**
     * Выполняет команду движения
     * ✅ ОБНОВЛЕНО: Теперь async для поддержки запроса выбора у игрока при escapable trap
     */
    async execute(command, isLastMove) {
        const result = {
            success: false,
            command,
            sideEffects: [],
            capturedTerritories: [],
            eliminatedSides: [],
        };
        // 1. Валидация хода
        const validation = this.validator.validateMove(command.sideId, command.from, command.to);
        if (!validation.isValid) {
            result.error = validation.reason || 'Invalid move';
            return result;
        }
        // 2. Получаем необходимые объекты
        const side = this.board.getSide(command.sideId);
        const fromTerritory = this.board.getTerritoryAt(command.from);
        const toTerritory = this.board.getTerritoryAt(command.to);
        if (!side || !fromTerritory || !toTerritory) {
            result.error = 'Invalid board state';
            return result;
        }
        // The board as the side saw it before the move — to tell, if the move ends
        // in a trap, whether any other move would not have (TrapTriggeredEvent.forced).
        const boardBeforeMove = this.board.clone();
        // 3. Выполняем ход (ловушки теперь обрабатываются ПОСЛЕ этапа захватов)
        // Освобождаем исходную позицию
        console.log(`[MoveExecutor] BEFORE vacate: ${command.from} owner=${fromTerritory.owner}, occupiedBy=${fromTerritory.occupiedBy}`);
        fromTerritory.vacate();
        console.log(`[MoveExecutor] AFTER vacate: ${command.from} owner=${fromTerritory.owner}, occupiedBy=${fromTerritory.occupiedBy}`);
        // Ход игрока: прямой захват целевой нейтральной клетки
        // Capture the owner + active-field flag BEFORE the capture() call, for the
        // 'direct' capture event below (usually neutral -> both null/false).
        const toPreviousOwner = toTerritory.owner;
        // Ходовое поле врага стоит 30 очков независимо от типа захвата, поэтому флаг
        // ОБЯЗАН уйти в capture(). Раньше он вычислялся только ради события ниже, а
        // capture() вызывался без него — прямое приземление на активное поле врага
        // всегда давало 10 очков вместо 30.
        const toWasActiveField = toPreviousOwner != null &&
            toPreviousOwner !== command.sideId &&
            this.board.isActiveFieldOf(toPreviousOwner, command.to);
        toTerritory.capture(command.sideId, toWasActiveField);
        toTerritory.occupy(command.sideId);
        side.captureTerritory(command.to);
        side.addTurnActiveField(command.to);
        // Clear all activeFields before setting new position — only the current
        // move's destination (+ its surrounding captures) should be active.
        // Previous move positions are regular territories worth 10 pts, not 30.
        this.clearActiveFieldsForSide(side);
        side.setActiveField(command.to, true);
        side.makeMove();
        side.setLastMovePosition(command.to);
        // 5. Публикуем событие хода
        this.eventBus.publish(new MoveMadeEvent(command.sideId, command.from, command.to, command.round, command.moveInRound));
        // Direct (active) capture of the landed-on tile. This is the ONLY emit site
        // for captureType 'direct' on the live path (the old CaptureEngine.directCapture
        // is never instantiated), so without it the tutorial's neutral-capture (#23) and
        // direct enemy/ally capture (#1/#19) hints never fire.
        this.eventBus.publish(new TerritoryCapturedEvent(command.to, toPreviousOwner, command.sideId, 'direct', toWasActiveField, undefined, 1));
        result.sideEffects?.push(`Move from ${command.from} to ${command.to}`);
        // 7. СНАЧАЛА выполняем все возможные захваты (включая цепные)
        const surroundingCaptures = this.resolveCaptureChains(command.to, command.sideId);
        const capturingSideId = command.sideId;
        const capturingSide = this.board.getSide(capturingSideId) ?? side;
        for (const captured of surroundingCaptures) {
            result.capturedTerritories?.push(captured.position);
            result.sideEffects?.push(`Captured ${captured.position} by surrounding (by side ${capturingSideId})`);
            // Добавляем захваченную территорию в turnActiveFields если она в пределах текущего хода
            // И ставим на неё фигуру для веерного движения
            const capturedTerritory = this.board.getTerritoryAt(captured.position);
            if (capturedTerritory) {
                capturedTerritory.occupy(capturingSideId);
                capturingSide.addTurnActiveField(captured.position);
                capturingSide.setActiveField(captured.position, true);
            }
            // Отмечаем территории, захваченные окружением
            // Они станут активными в следующем раунде
            capturingSide.addCapturedOnLastMove(captured.position);
        }
        // 7.5. ✅ AGGRESSION PENALTY: Если захвачены вражеские территории окружением,
        // прямой захват нейтральной территории НЕ даёт 3 очка
        if (surroundingCaptures.length > 0) {
            const hasCapturedEnemy = surroundingCaptures.some(c => c.previousOwner !== null);
            if (hasCapturedEnemy) {
                const directCaptureTerritory = this.board.getTerritoryAt(command.to);
                // Применяем штраф только если прямой захват был нейтральной территории
                if (directCaptureTerritory && directCaptureTerritory.previousOwner === null) {
                    directCaptureTerritory.setIsScoringPenalized(true);
                    console.log(`[MoveExecutor] ⚠️ Aggression penalty applied to ${command.to} (captured enemy by surrounding)`);
                    result.sideEffects?.push(`Aggression penalty applied to ${command.to}`);
                }
            }
        }
        // 7.55. Tutorial semi-encirclement warning (#12): after this move's captures,
        // emit for any group now exactly one neutral cell from full encirclement,
        // limited to groups this move actually touched (no spam). Observational only —
        // a pure read of the post-capture board, no mutation, no outcome change.
        {
            const touchedKeys = new Set([command.to.toKey()]);
            for (const c of surroundingCaptures)
                touchedKeys.add(c.position.toKey());
            for (const semi of this.groupAnalyzer.getSemiSurroundedGroups(touchedKeys)) {
                this.eventBus.publish(new SemiEncirclementEvent(semi.owner, semi.openPosition, semi.surroundingSides));
            }
        }
        // 7.6. ПОСЛЕ этапа захватов проверяем и разрешаем ловушку
        const isTrapped = this.validator.checkForTrap(command.to, command.sideId);
        console.log('[MoveExecutor] Post-capture trap check:', isTrapped, 'at', command.to.toString());
        if (isTrapped) {
            const forced = this.everyMoveLandsInTrap(boardBeforeMove, command.sideId);
            const trapResolutionData = await this.resolveTrapSynchronously(command.sideId, command.to, forced, command.from);
            result.triggeredTrap = {
                position: command.to,
                trappedSide: command.sideId,
                canEscape: trapResolutionData?.situation.canEscape ?? false,
                resolution: trapResolutionData ? {
                    situation: trapResolutionData.situation,
                    resolution: trapResolutionData.resolution,
                    choice: trapResolutionData.choice,
                    deferred: trapResolutionData.deferred,
                } : undefined,
            };
            result.sideEffects?.push(`Side ${command.sideId} trapped at ${command.to}`);
        }
        // 7.7. Detect player's own surrounded groups (multi-cell) that still have moves available.
        // Single-field trap at command.to is already handled above; this catches larger groups.
        await this.checkAndHandleSurroundedPlayerGroups(command, result, isLastMove);
        // 8. Проверяем элиминацию сторон
        this.reconcileActiveFieldPools();
        const eliminatedSides = this.checkEliminations();
        for (const eliminatedId of eliminatedSides) {
            result.eliminatedSides?.push(eliminatedId);
            result.sideEffects?.push(`Side ${eliminatedId} eliminated`);
            this.eventBus.publish(new SideEliminatedEvent(eliminatedId, 'no-moves', command.sideId));
        }
        // 9. Сохраняем команду в истории
        this.moveHistory.push(command);
        result.success = true;
        console.log(`[MoveExecutor] ✅ Move completed successfully: ${command.from} -> ${command.to} for side ${command.sideId}`);
        return result;
    }
    resolveCaptureChains(seedPosition, movingSideId) {
        const allCaptures = [];
        const queue = [seedPosition];
        const seen = new Set();
        while (queue.length > 0) {
            const current = queue.shift();
            const key = current.toKey();
            if (seen.has(key)) {
                continue;
            }
            seen.add(key);
            const captures = this.checkSurroundingCaptures(current, movingSideId);
            if (captures.length === 0) {
                continue;
            }
            allCaptures.push(...captures);
            for (const captured of captures) {
                queue.push(captured.position);
            }
        }
        const capturedKeys = new Set(allCaptures.map(c => c.position.toKey()));
        const globalCaptures = this.resolveGlobalSurroundedChains(movingSideId, capturedKeys);
        allCaptures.push(...globalCaptures);
        // Chain capture (#18): one move's surround cascaded across multiple victims.
        // Emit a single summary CascadeCaptureEvent so the tutorial chain hint fires
        // (the per-cell 'surrounding' events still fire too — different hints). This is
        // the only live emitter on the main move path; the dead CaptureEngine.massCapture
        // never runs. Single-victim group captures stay out (covered by group hints).
        const affectedSides = [...new Set(allCaptures.map(c => c.previousOwner).filter((o) => o != null))];
        if (affectedSides.length >= 2) {
            this.eventBus.publish(new CascadeCaptureEvent(movingSideId, allCaptures.map(c => c.position), affectedSides));
        }
        return allCaptures;
    }
    /**
     * Global recursive sweep:
     * if conquers create new fully surrounded groups anywhere on the board,
     * keep conquering until board state stabilizes.
     */
    resolveGlobalSurroundedChains(movingSideId, capturedKeys) {
        const captured = [];
        let changed = true;
        while (changed) {
            changed = false;
            const groups = this.groupAnalyzer.findAllGroups();
            for (const group of groups) {
                if (!group.isSurrounded || group.owner === null) {
                    continue;
                }
                // If current player's own group became an enclosed island,
                // DO NOT resolve it here. Trap ownership for the mover is handled by
                // the trap flow (deferred for ESCAPABLE, immediate for forced) later
                // in execute(). This keeps "move into trap" behavior consistent:
                // surrounding captures happen now, trap capture happens at turn end.
                if (group.owner === movingSideId) {
                    continue;
                }
                // Group identity for the tutorial (single vs group vs mixed capture).
                const groupSize = group.positions.length;
                const groupId = `${group.owner}@${group.positions.reduce((m, p) => { const k = p.toKey(); return k < m ? k : m; }, group.positions[0].toKey())}`;
                for (const position of group.positions) {
                    const key = position.toKey();
                    const territory = this.board.getTerritoryAt(position);
                    if (!territory || territory.owner === movingSideId) {
                        continue;
                    }
                    if (!territory.canBeCaptured()) {
                        continue;
                    }
                    if (capturedKeys.has(key)) {
                        continue;
                    }
                    const previousOwner = territory.owner;
                    const captorSideId = movingSideId;
                    let wasActiveField = false;
                    if (previousOwner !== null && previousOwner !== captorSideId) {
                        const previousOwnerSide = this.board.getSide(previousOwner);
                        if (previousOwnerSide) {
                            wasActiveField = this.isEnemyCriticalField(position, previousOwner, territory.owner);
                            previousOwnerSide.loseTerritory(position);
                        }
                    }
                    // #D2: флаг передаётся в capture() и сбрасывается при перезахвате
                    territory.capture(captorSideId, wasActiveField);
                    const capturingSide = this.board.getSide(captorSideId);
                    if (capturingSide) {
                        capturingSide.captureTerritory(position);
                        capturingSide.addTurnActiveField(position);
                    }
                    this.eventBus.publish(new TerritoryCapturedEvent(position, previousOwner, captorSideId, 'surrounding', wasActiveField, groupId, groupSize));
                    if (territory.occupiedBy !== null) {
                        territory.vacate();
                    }
                    captured.push({ position, previousOwner });
                    capturedKeys.add(key);
                    changed = true;
                }
            }
        }
        return captured;
    }
    /**
     * Проверяет окружение территорий после хода
     * Использует анализ групп для проверки полного окружения
     *
     * @param movedTo - Позиция куда был сделан ход
     * @param movingSideId - ID стороны, которая СДЕЛАЛА ход (не текущий владелец территории!)
     *
     * ИСПРАВЛЕНИЕ: Теперь принимает movingSideId как параметр вместо чтения owner с территории.
     * Это важно потому что после trap resolution владелец территории может измениться,
     * но surrounding capture должен кредитоваться стороне, которая СДЕЛАЛА ход.
     */
    checkSurroundingCaptures(movedTo, movingSideId) {
        const captured = [];
        const movingSide = movingSideId;
        if (movingSide === null || movingSide === undefined) {
            return captured;
        }
        console.log(`[Surrounding] Checking for completed group surroundings after move to ${movedTo}`);
        // Проверяем, какие группы были завершены этим ходом
        const completedGroups = this.groupAnalyzer.wouldCompleteSurrounding(movedTo, movingSide);
        // Восстанавливаем состояние (wouldCompleteSurrounding временно меняло владельца)
        const territory = this.board.getTerritoryAt(movedTo);
        if (territory) {
            territory.capture(movingSide);
        }
        for (const group of completedGroups) {
            console.log(`[Surrounding] Group of Side ${group.owner} (${group.positions.length} territories) is fully surrounded`);
            // Group identity for the tutorial (single vs group vs mixed capture).
            const groupSize = group.positions.length;
            const groupId = `${group.owner}@${group.positions.reduce((m, p) => { const k = p.toKey(); return k < m ? k : m; }, group.positions[0].toKey())}`;
            // Захватываем все территории в окруженной группе
            for (const position of group.positions) {
                const groupTerritory = this.board.getTerritoryAt(position);
                if (!groupTerritory || groupTerritory.owner === movingSide) {
                    continue;
                }
                // FIX: Skip if territory cannot be captured (BURNED state)
                if (!groupTerritory.canBeCaptured()) {
                    console.log(`[MoveExecutor] Skipping ${position} - cannot be captured (state: ${groupTerritory.state})`);
                    continue;
                }
                const previousOwner = groupTerritory.owner;
                let wasActiveField = false;
                // ✅ FIX: Сохраняем флаг wasEnemyActiveField ПЕРЕД loseTerritory
                if (previousOwner !== null && previousOwner !== movingSide) {
                    const previousOwnerSide = this.board.getSide(previousOwner);
                    if (previousOwnerSide) {
                        wasActiveField = this.isEnemyCriticalField(position, previousOwner, groupTerritory.owner);
                        previousOwnerSide.loseTerritory(position);
                    }
                }
                // Захватываем территорию (#D2: флаг через параметр, сбрасывается при перезахвате)
                groupTerritory.capture(movingSide, wasActiveField);
                // ВАЖНО: Добавляем территорию в turnActiveFields нового владельца
                // Это позволяет делать цепные ходы с захваченных территорий
                const capturingSide = this.board.getSide(movingSide);
                if (capturingSide) {
                    capturingSide.captureTerritory(position);
                    capturingSide.addTurnActiveField(position);
                }
                // Публикуем событие захвата
                this.eventBus.publish(new TerritoryCapturedEvent(position, previousOwner, movingSide, 'surrounding', wasActiveField, groupId, groupSize));
                captured.push({ position, previousOwner });
                // Если на территории была фигура, она уничтожается
                if (groupTerritory.occupiedBy !== null) {
                    groupTerritory.vacate();
                }
                console.log(`[Surrounding] Captured territory ${position} as part of surrounded group`);
            }
        }
        return captured;
    }
    /**
     * A captured enemy territory gives +30 only if it was an active field
     * for the previous owner at the exact capture moment (regardless of whether
     * the owner could actually move from it).
     * Clears all activeFields for a side.
     * Called before each move so only the current move's destination
     * (and its surrounding captures) remain as active fields.
     */
    clearActiveFieldsForSide(side) {
        for (const key of side.activeFields) {
            side.setActiveField(Position.fromKey(key), false);
        }
    }
    /**
     * ВАЖНО: Проверяем activeFields (поля, с которых можно ходить СЕЙЧАС),
     * а не turnActiveFields (которые включают поля для следующего раунда)
     */
    isEnemyCriticalField(position, previousOwner, currentTerritoryOwner) {
        if (currentTerritoryOwner !== previousOwner) {
            return false;
        }
        const previousOwnerSide = this.board.getSide(previousOwner);
        if (!previousOwnerSide) {
            return false;
        }
        // Being an active field is sufficient — even if the player can't move from it,
        // it is still an active field and capturing it should give 30 points.
        return previousOwnerSide.isActiveField(position);
    }
    /**
     * Keeps active field pools synchronized with current ownership.
     * Called after every executed move.
     */
    reconcileActiveFieldPools() {
        for (const [sideId, side] of this.board.sides) {
            for (const fieldKey of side.turnActiveFields) {
                const position = Position.fromKey(fieldKey);
                const territory = this.board.getTerritoryAt(position);
                if (!territory || territory.owner !== sideId) {
                    side.removeTurnActiveField(position);
                    side.setActiveField(position, false);
                }
            }
            for (const fieldKey of side.activeFields) {
                const position = Position.fromKey(fieldKey);
                const territory = this.board.getTerritoryAt(position);
                if (!territory || territory.owner !== sideId) {
                    side.setActiveField(position, false);
                }
            }
        }
    }
    /**
     * Проверяет элиминацию сторон
     * ВАЖНО: НЕ элиминируем игроков без ходов - они остаются в игре!
     * Элиминация происходит только при полной потере всех территорий
     */
    checkEliminations() {
        const eliminated = [];
        for (const [sideId, side] of this.board.sides) {
            // Элиминируем ТОЛЬКО если у стороны нет территорий вообще
            // НЕ элиминируем если просто нет доступных ходов
            if (side.isActive && side.territories.size === 0) {
                side.eliminate();
                eliminated.push(sideId);
                console.log(`[MoveExecutor] Side ${sideId} eliminated - no territories left`);
            }
            // Note: Sides without moves stay active - they can receive territories later via traps/surrounding
        }
        return eliminated;
    }
    /**
     * Возвращает историю ходов
     */
    getMoveHistory() {
        return [...this.moveHistory];
    }
    /**
     * Очищает историю ходов
     */
    clearHistory() {
        this.moveHistory = [];
    }
    /**
     * Отменяет последний ход (для будущей реализации)
     */
    undo() {
        // TODO: Реализовать отмену хода
        // Требует сохранения состояния доски перед каждым ходом
        throw new Error('Undo not implemented yet');
    }
    /**
     * ✅ ОБНОВЛЕНО: Асинхронная обработка ловушки с запросом выбора у игрока
     * Вызывается сразу после попадания в ловушку
     * Для ESCAPABLE trap запрашивает выбор у игрока через SyncPlayerChoiceHandler
     * RETURNS: Объект с данными ситуации и разрешения для отправки клиенту
     */
    async resolveTrapSynchronously(sideId, position, forced, fromPosition) {
        const side = this.board.getSide(sideId);
        if (!side) {
            console.error(`[MoveExecutor] Cannot resolve trap: side ${sideId} not found`);
            return null;
        }
        console.log(`[MoveExecutor] 🔧 Starting synchronous trap resolution for side ${sideId} at ${position}`);
        // Анализируем ситуацию ловушки
        const situation = this.trapResolver.analyzeSituation(position, side);
        if (fromPosition)
            situation.fromPosition = fromPosition;
        console.log(`[MoveExecutor] Trap situation analyzed: type=${situation.type}, canEscape=${situation.canEscape}`);
        // The trap as it is resolved — not MovementValidator.getTrapInfo's guess,
        // which ran a move late and did not know round 1's single move.
        this.eventBus.publish(new TrapTriggeredEvent(sideId, position, situation.surroundingSides, situation.canEscape, TRAP_TYPE[situation.type], forced));
        // Обрабатываем по типу ситуации
        switch (situation.type) {
            case TrapSituationType.SINGLE_COLOR:
                // Ситуация III: Монополия - мгновенный захват одной стороной
                console.log(`[MoveExecutor] Resolving SINGLE_COLOR trap (monopoly by side ${situation.dominantSide})`);
                const instantResolution = this.trapResolver.resolveInstant(situation);
                this.handleTrapResolution(situation, instantResolution);
                return { situation, resolution: instantResolution };
            case TrapSituationType.FORCE_BASED:
                // Ситуация II: По силе - автоматический расчет сильнейшего
                console.log('[MoveExecutor] Resolving FORCE_BASED trap (automatic by force calculation)');
                const forceResolution = this.trapResolver.resolveByForce(situation);
                this.handleTrapResolution(situation, forceResolution);
                return { situation, resolution: forceResolution };
            case TrapSituationType.ESCAPABLE:
                // Ситуация I: Игрок выбирает кому отдать поле - разрешается В КОНЦЕ ХОДА
                // Игрок может сбежать из ловушки до конца хода, тогда передача не состоится.
                console.log('[MoveExecutor] ESCAPABLE trap detected - deferring capture to end of turn');
                let choice;
                if (this.syncPlayerChoiceHandler) {
                    console.log('[MoveExecutor] Requesting choice through SyncPlayerChoiceHandler...');
                    choice = await this.syncPlayerChoiceHandler.requestChoice(situation, sideId, 20000 // 20 seconds timeout
                    );
                }
                else {
                    console.warn('[MoveExecutor] ⚠️ SyncPlayerChoiceHandler not available, using fallback AI choice');
                    choice = this.makeAIChoice(situation);
                }
                console.log(`[MoveExecutor] Choice received: give territory to side ${choice.giveTerritoryTo} (DEFERRED to turn end)`);
                // Store as pending trap — actual territory transfer happens at turn end via resolvePendingTraps.
                // If the player escapes (moves away so the surrounding breaks), the pending trap is cancelled.
                const escapableSide = this.board.getSide(sideId);
                if (escapableSide) {
                    escapableSide.addPendingTrap({
                        position,
                        giveTerritoryTo: choice.giveTerritoryTo,
                        surroundingSides: situation.surroundingSides,
                        createdAtMove: escapableSide.movesInCurrentRound,
                    });
                }
                return {
                    situation,
                    resolution: { territoryCapturedBy: choice.giveTerritoryTo, sideEliminated: false },
                    choice,
                    deferred: true,
                };
            default:
                console.error(`[MoveExecutor] Unknown trap situation type: ${situation.type}`);
                return null;
        }
    }
    /**
     * Forced self-encirclement (§4.11): every legal move the side had before this
     * one ends in a trap. Each move is played on a copy of that board as far as its
     * own captures — they can open the landing field — and then checked.
     */
    everyMoveLandsInTrap(before, sideId) {
        const moves = new MovementValidator(before).getAvailableMovesForSide(sideId);
        for (const [fromKey, targets] of moves) {
            for (const to of targets) {
                if (!this.landsInTrap(before, sideId, Position.fromKey(fromKey), to))
                    return false;
            }
        }
        return moves.size > 0;
    }
    landsInTrap(before, sideId, from, to) {
        const board = before.clone();
        const side = board.getSide(sideId);
        const landing = board.getTerritoryAt(to);
        if (!side || !landing)
            return false;
        board.getTerritoryAt(from)?.vacate();
        landing.capture(sideId);
        landing.occupy(sideId);
        side.captureTerritory(to);
        side.addTurnActiveField(to);
        this.clearActiveFieldsForSide(side);
        side.setActiveField(to, true);
        const bus = new EventBus();
        const sandbox = new MoveExecutor(board, bus, new TrapResolver(board, bus));
        sandbox.resolveCaptureChains(to, sideId);
        return sandbox.validator.checkForTrap(to, sideId);
    }
    /**
     * AI выбор при escapable trap: использует ForceCalculator для определения сильнейшей стороны
     * ✅ ИСПРАВЛЕНО: Теперь использует правильный алгоритм подсчета сил из дизайн-документа
     * Алгоритм: ортогональные (вес 3) → диагональные (вес 1) → глобальные (вес 0.01) → паритет
     * Используется только как fallback, когда SyncPlayerChoiceHandler недоступен
     */
    makeAIChoice(situation) {
        if (situation.surroundingSides.length === 0) {
            throw new Error('[MoveExecutor] Cannot make AI choice: no surrounding sides');
        }
        // ✅ ИСПРАВЛЕНИЕ: Используем ForceCalculator для правильного подсчета сил
        const forces = this.trapResolver.forceCalculator.calculateForces(situation.trappedPosition, situation.trappedSide);
        console.log('[MoveExecutor] Force calculation for fallback AI choice:');
        for (const force of forces) {
            console.log(`  Side ${force.sideId}: total=${force.totalForce.toFixed(2)} (orth=${force.orthogonalForce}, diag=${force.diagonalForce}, global=${force.globalForce.toFixed(2)})`);
        }
        // Определяем победителя по силам
        const winner = this.trapResolver.forceCalculator.determineWinner(forces);
        if (winner === null) {
            // Паритет - выбираем первую окружающую сторону как fallback
            console.warn('[MoveExecutor] Force parity detected in escapable trap fallback, choosing first surrounding side');
            return {
                giveTerritoryTo: situation.surroundingSides[0],
                reason: 'strategic',
            };
        }
        console.log(`[MoveExecutor] Force-based fallback: territory to side ${winner} (strongest by force calculation)`);
        return {
            giveTerritoryTo: winner,
            reason: 'strategic',
        };
    }
    /**
     * Обработка результата разрешения ловушки
     * Публикует события и обрабатывает последствия
     */
    handleTrapResolution(situation, resolution) {
        console.log('[MoveExecutor] 📤 Handling trap resolution result');
        console.log(`  Territory captured by: ${resolution.territoryCapturedBy}`);
        console.log(`  Side eliminated: ${resolution.sideEliminated}`);
        console.log(`  Burned: ${resolution.burned ?? false}`);
        if (resolution.burned && !this.isProcessingBurningLandAftermath) {
            this.pendingBurningLandAftermath = true;
        }
        // Публикуем событие разрешения ловушки
        const resolutionType = resolution.burned ? 'burned' : 'captured';
        this.eventBus.publish(new TrapResolvedEvent(situation.trappedSide, situation.trappedPosition, resolution.territoryCapturedBy, resolutionType));
        // Force-trap perfect-parity burn: the contested field is lost to all. Emit the
        // (previously never-published) TerritoryBurnedEvent so the tutorial scorched-field
        // hint (#14) fires. Cadenced with TrapResolvedEvent('burned') — one per resolution.
        if (resolution.burned) {
            this.eventBus.publish(new TerritoryBurnedEvent(situation.trappedPosition, 'parity'));
        }
        // Проверяем элиминацию захваченной стороны
        if (resolution.sideEliminated) {
            const side = this.board.getSide(situation.trappedSide);
            if (side && side.isActive) {
                side.eliminate();
                this.eventBus.publish(new SideEliminatedEvent(situation.trappedSide, 'surrounded', this.board.currentSide, resolution.territoryCapturedBy >= 0 ? resolution.territoryCapturedBy : undefined));
                console.log(`[MoveExecutor] ⚰️ Side ${situation.trappedSide} eliminated due to trap`);
            }
        }
        console.log('[MoveExecutor] ✅ Trap resolution complete');
    }
    /**
     * Re-scans all owned territories after a burn event.
     * Burned land acts as a hard wall, so new forced traps can appear globally.
     * We resolve only forced outcomes (single-color / force-based), matching conquer flow.
     */
    processBurningLandAftermath(result) {
        this.isProcessingBurningLandAftermath = true;
        try {
            const maxPasses = BOARD_SIZE * BOARD_SIZE;
            let pass = 0;
            let changed = true;
            while (changed && pass < maxPasses) {
                changed = false;
                pass++;
                const snapshot = Array.from(this.board.territories.entries())
                    .map(([key, territory]) => ({ key, owner: territory.owner }));
                for (const item of snapshot) {
                    if (item.owner === null) {
                        continue;
                    }
                    const position = Position.fromKey(item.key);
                    const currentTerritory = this.board.getTerritoryAt(position);
                    // Skip stale snapshot rows that already changed this pass.
                    if (!currentTerritory || currentTerritory.owner !== item.owner) {
                        continue;
                    }
                    const isTrapped = this.validator.checkForTrap(position, item.owner);
                    if (!isTrapped) {
                        continue;
                    }
                    const situation = this.buildForcedBurnAftermathSituation(position, item.owner);
                    if (!situation) {
                        continue;
                    }
                    const resolution = situation.type === TrapSituationType.SINGLE_COLOR
                        ? this.trapResolver.resolveInstant(situation)
                        : this.trapResolver.resolveByForce(situation);
                    this.handleTrapResolution(situation, resolution);
                    changed = true;
                    result.sideEffects?.push(`Burn aftermath resolved at ${position.toString()}`);
                    if (resolution.territoryCapturedBy >= 0) {
                        result.capturedTerritories?.push(position);
                    }
                    if (resolution.sideEliminated && !result.eliminatedSides?.includes(situation.trappedSide)) {
                        result.eliminatedSides?.push(situation.trappedSide);
                    }
                }
            }
        }
        finally {
            this.isProcessingBurningLandAftermath = false;
        }
    }
    flushPendingBurningLandAftermath(result) {
        while (this.pendingBurningLandAftermath) {
            this.pendingBurningLandAftermath = false;
            this.processBurningLandAftermath(result);
        }
    }
    /**
     * End-of-turn global capture sweep.
     * Re-scans the full board for trap/capture outcomes and resolves them
     * before control is passed to the next side.
     *
     * For multi-side surroundings at end-turn, we always use choice-based
     * resolution for the trapped side (same popup flow as regular trap choice).
     */
    async resolveBoardWideCapturesAtTurnEnd(excludePositions) {
        const result = {
            success: true,
            sideEffects: [],
            capturedTerritories: [],
            eliminatedSides: [],
        };
        // NEW: Group-based conquest resolution
        await this.resolveGroupBasedConquest(result);
        const maxPasses = BOARD_SIZE * BOARD_SIZE;
        let pass = 0;
        let changed = true;
        while (changed && pass < maxPasses) {
            changed = false;
            pass++;
            const snapshot = Array.from(this.board.territories.entries())
                .map(([key, territory]) => ({ key, owner: territory.owner }));
            for (const item of snapshot) {
                if (item.owner === null) {
                    continue;
                }
                // Skip positions that were just resolved by resolvePendingTraps.
                // Those recipients (possibly reanimated) are still surrounded, so
                // re-running trap logic here would immediately recapture them.
                if (excludePositions?.has(item.key)) {
                    console.log(`[MoveExecutor] Skipping recently resolved trap position: ${item.key}`);
                    continue;
                }
                const position = Position.fromKey(item.key);
                const territory = this.board.getTerritoryAt(position);
                if (!territory || territory.owner !== item.owner) {
                    continue;
                }
                const trappedSideId = item.owner;
                const trappedSide = this.board.getSide(trappedSideId);
                if (!trappedSide) {
                    continue;
                }
                const isTrapped = this.validator.checkForTrap(position, trappedSideId);
                if (!isTrapped) {
                    continue;
                }
                const analyzed = this.trapResolver.analyzeSituation(position, trappedSide);
                if (analyzed.surroundingSides.length === 0) {
                    continue;
                }
                // #D2: флаг wasEnemyActiveField теперь выставляется внутри TrapResolver
                // в момент capture() (ловушечное поле всегда было активным → +30).
                // Прежний предварительный set здесь всё равно перезаписывался TrapResolver,
                // поэтому убран как мёртвый код.
                let resolution;
                let situation = analyzed;
                if (analyzed.type === TrapSituationType.SINGLE_COLOR) {
                    resolution = this.trapResolver.resolveInstant(analyzed);
                }
                else {
                    // End-turn rule: when surrounded by multiple sides, trapped player chooses recipient.
                    const choiceSituation = {
                        type: TrapSituationType.ESCAPABLE,
                        trappedSide: analyzed.trappedSide,
                        trappedPosition: analyzed.trappedPosition,
                        surroundingSides: analyzed.surroundingSides,
                        canEscape: false,
                    };
                    situation = choiceSituation;
                    let choice;
                    if (this.syncPlayerChoiceHandler) {
                        choice = await this.syncPlayerChoiceHandler.requestChoice(choiceSituation, trappedSideId, 20000);
                    }
                    else {
                        choice = this.makeAIChoice(choiceSituation);
                    }
                    resolution = this.trapResolver.resolveByChoice(choiceSituation, choice);
                }
                this.handleTrapResolution(situation, resolution);
                changed = true;
                result.sideEffects?.push(`End-turn capture resolved at ${position.toString()}`);
                if (resolution.territoryCapturedBy >= 0) {
                    result.capturedTerritories?.push(position);
                }
                if (resolution.sideEliminated && !result.eliminatedSides?.includes(situation.trappedSide)) {
                    result.eliminatedSides?.push(situation.trappedSide);
                }
            }
        }
        // Burned-land geometry changes should be resolved only after the rest of
        // the turn-end captures and trap resolutions have finished.
        this.flushPendingBurningLandAftermath(result);
        return result.capturedTerritories?.length || 0;
    }
    buildForcedBurnAftermathSituation(position, trappedSideId) {
        const trappedSide = this.board.getSide(trappedSideId);
        if (!trappedSide) {
            return null;
        }
        const surroundingSides = new Set();
        for (const neighbor of position.getOrthogonalNeighbors()) {
            const territory = this.board.getTerritoryAt(neighbor);
            if (territory && territory.owner !== null && territory.owner !== trappedSideId) {
                surroundingSides.add(territory.owner);
            }
        }
        const sides = Array.from(surroundingSides);
        if (sides.length === 0) {
            return null;
        }
        if (sides.length === 1) {
            const dominantSide = sides[0];
            if (dominantSide === undefined) {
                return null;
            }
            return {
                type: TrapSituationType.SINGLE_COLOR,
                trappedSide: trappedSideId,
                trappedPosition: position,
                surroundingSides: sides,
                canEscape: false,
                dominantSide,
            };
        }
        return {
            type: TrapSituationType.FORCE_BASED,
            trappedSide: trappedSideId,
            trappedPosition: position,
            surroundingSides: sides,
            canEscape: false,
        };
    }
    /**
     * Проверяет, сбежал ли игрок из отложенных ловушек после хода
     * Побег = окружение разомкнуто (появился свободный ортогональный сосед)
     * Вызывается после каждого хода
     */
    checkPendingTrapEscapes(sideId) {
        const side = this.board.getSide(sideId);
        if (!side || !side.hasPendingTraps()) {
            return [];
        }
        const escapedTraps = [];
        for (const trap of side.getPendingTraps()) {
            // Проверяем, разомкнуто ли окружение
            const isStillTrapped = this.validator.checkForTrap(trap.position, sideId);
            if (!isStillTrapped) {
                // Игрок сбежал! Удаляем pending trap
                console.log(`[MoveExecutor] 🏃 Side ${sideId} ESCAPED from trap at ${trap.position.toString()}!`);
                side.removePendingTrap(trap.position);
                escapedTraps.push(trap);
                this.eventBus.publish(new TrapResolvedEvent(sideId, trap.position, sideId, 'escaped'));
            }
            else {
                console.log(`[MoveExecutor] ⏳ Trap at ${trap.position.toString()} still active (waiting for escape or round end)`);
            }
        }
        return escapedTraps;
    }
    /**
     * Checks if any pending group conquests are no longer valid (group broke free of surrounding).
     * Mirrors checkPendingTrapEscapes — called after every move.
     */
    checkPendingGroupConquestEscapes(sideId) {
        const side = this.board.getSide(sideId);
        if (!side || !side.hasPendingGroupConquests())
            return [];
        const cancelledConquests = [];
        const allGroups = this.groupAnalyzer.findAllGroups();
        for (const conquest of side.getPendingGroupConquests()) {
            const conquestKeys = new Set(conquest.positions.map(p => p.toKey()));
            const matchingGroup = allGroups.find(g => g.owner === sideId && g.positions.some(p => conquestKeys.has(p.toKey())));
            if (!matchingGroup || !matchingGroup.isSurrounded) {
                console.log(`[MoveExecutor] 🏃 Side ${sideId} group conquest cancelled — group is no longer surrounded`);
                side.removePendingGroupConquest(conquest);
                cancelledConquests.push(conquest);
            }
        }
        return cancelledConquests;
    }
    /**
     * Разрешает все отложенные ловушки для стороны
     * Вызывается в конце раунда стороны или когда она больше не может ходить
     * Возвращает массив разрешений
     */
    resolvePendingTraps(sideId, resolvedPositions) {
        const side = this.board.getSide(sideId);
        if (!side || !side.hasPendingTraps()) {
            return [];
        }
        console.log(`[MoveExecutor] 🔓 Resolving ${side.getPendingTraps().length} pending traps for side ${sideId}`);
        const resolutions = [];
        for (const trap of side.getPendingTraps()) {
            // #D2: +30 за ловушечное поле выставляется внутри TrapResolver в момент
            // capture(). Прежний предварительный set здесь перезаписывался безусловным
            // флагом TrapResolver, поэтому убран как мёртвый код.
            // Создаём TrapSituation для resolveByChoice
            const situation = {
                type: TrapSituationType.ESCAPABLE,
                trappedSide: sideId,
                trappedPosition: trap.position,
                surroundingSides: trap.surroundingSides,
                canEscape: false, // Уже не может - раунд закончился
            };
            const choice = {
                giveTerritoryTo: trap.giveTerritoryTo,
                reason: 'strategic',
            };
            console.log(`[MoveExecutor] Resolving deferred trap at ${trap.position.toString()} -> give to side ${trap.giveTerritoryTo}`);
            // Теперь разрешаем ловушку по-настоящему
            const resolution = this.trapResolver.resolveByChoice(situation, choice);
            this.handleTrapResolution(situation, resolution);
            resolutions.push(resolution);
            // Track this position so resolveBoardWideCapturesAtTurnEnd skips it.
            // The recipient (potentially reanimated) is still surrounded at this position,
            // so without exclusion the board-wide sweep would immediately recapture it.
            resolvedPositions?.add(trap.position.toKey());
            // Публикуем событие разрешения отложенной ловушки
            this.eventBus.publish(new TrapResolvedEvent(sideId, trap.position, trap.giveTerritoryTo, 'captured'));
        }
        // Очищаем все pending traps после разрешения
        side.clearPendingTraps();
        return resolutions;
    }
    /**
     * Проверяет, есть ли у стороны отложенные ловушки
     */
    hasPendingTraps(sideId) {
        const side = this.board.getSide(sideId);
        return side?.hasPendingTraps() ?? false;
    }
    // ── Group Surrender (new feature) ────────────────────────────────────────
    /**
     * After a move, scans the moving player's groups for ones that are:
     *   - Multi-cell (size >= 2),
     *   - Fully surrounded by enemies / burned land / board edges, AND
     *   - Have at least one valid knight move available from within the group.
     *
     * When found, a CHOICE_REQUEST popup is shown so the player can pick a recipient side.
     * The choice is stored as a PendingGroupConquest and resolved at turn end.
     */
    async checkAndHandleSurroundedPlayerGroups(command, result, isLastMove) {
        const side = this.board.getSide(command.sideId);
        if (!side)
            return;
        // If this is the last move of the turn, skip popup entirely.
        // The surrounded groups will be captured by force via resolveGroupBasedConquest() at turn end.
        if (isLastMove) {
            console.log(`[MoveExecutor] ⏭ Last move of turn for side ${command.sideId} — skipping group surrender popup (force capture at turn end)`);
            return;
        }
        // Find all connected groups owned by the moving side.
        const allGroups = this.groupAnalyzer.findAllGroups();
        const ownSurroundedGroups = allGroups.filter(g => g.owner === command.sideId && g.isSurrounded && g.positions.length >= 2);
        if (ownSurroundedGroups.length === 0)
            return;
        for (const group of ownSurroundedGroups) {
            // Only trigger if at least one ACTIVE field (turnActiveField) in the group has a valid move.
            // Non-active positions may have knight destinations but the player can't move from them.
            const hasMovesFromGroup = group.positions.some(pos => side.turnActiveFields.has(pos.toKey()) &&
                this.validator.getAvailableMovesFrom(pos, command.sideId).length > 0);
            if (!hasMovesFromGroup) {
                console.log(`[MoveExecutor] ⏭ Surrounded group (size ${group.positions.length}) for side ${command.sideId} has no available moves from active fields — skipping popup`);
                continue;
            }
            // Skip if this group already has a pending conquest choice recorded (deduplication).
            const pendingConquests = side.getPendingGroupConquests();
            const groupKeys = new Set(group.positions.map(p => p.toKey()));
            const alreadyPending = pendingConquests.some(pc => pc.positions.some(p => groupKeys.has(p.toKey())));
            if (alreadyPending) {
                console.log(`[MoveExecutor] ⏭ Group already has pending conquest — skipping popup`);
                continue;
            }
            // Collect unique surrounding sides from the group's perimeter.
            const surroundingSet = new Set();
            for (const perimPos of group.perimeter) {
                const t = this.board.getTerritoryAt(perimPos);
                if (t && t.owner !== null && t.owner !== command.sideId) {
                    surroundingSet.add(t.owner);
                }
            }
            const surroundingSides = Array.from(surroundingSet);
            if (surroundingSides.length === 0)
                continue;
            console.log(`[MoveExecutor] 🔲 Surrounded group (size ${group.positions.length}) detected for side ${command.sideId}. ` +
                `Surrounding sides: [${surroundingSides}]`);
            let chosenSide;
            if (this.syncPlayerChoiceHandler) {
                chosenSide = await this.syncPlayerChoiceHandler.requestGroupConquestChoice({ positions: group.positions, surroundingSides }, command.sideId, 20000);
            }
            else {
                // Fallback: pick strongest surrounding side by force calculation
                const forces = this.trapResolver.forceCalculator.calculateForces(group.positions[0], command.sideId);
                const winner = this.trapResolver.forceCalculator.determineWinner(forces);
                chosenSide = winner ?? surroundingSides[0];
            }
            console.log(`[MoveExecutor] Group conquest choice: give group to side ${chosenSide} (deferred)`);
            // Store as pending – will be resolved at turn end.
            side.addPendingGroupConquest({
                positions: group.positions,
                giveTerritoryTo: chosenSide,
                surroundingSides,
                createdAtMove: side.movesInCurrentRound,
            });
            result.sideEffects?.push(`Group of ${group.positions.length} territories (side ${command.sideId}) surrender deferred → side ${chosenSide}`);
        }
    }
    /**
     * Whether the side has pending group conquests to resolve at turn end.
     */
    hasPendingGroupConquests(sideId) {
        const side = this.board.getSide(sideId);
        return side?.hasPendingGroupConquests() ?? false;
    }
    /**
     * Resolves all pending group conquests for a side.
     * Called at turn end (after resolvePendingTraps).
     * Transfers each group's territories to the chosen recipient side.
     */
    resolvePendingGroupConquests(sideId) {
        const side = this.board.getSide(sideId);
        if (!side || !side.hasPendingGroupConquests())
            return;
        const conquests = side.getPendingGroupConquests();
        console.log(`[MoveExecutor] 🔓 Resolving ${conquests.length} pending group conquest(s) for side ${sideId}`);
        let lastRecipient;
        for (const conquest of conquests) {
            const receivingSide = this.board.getSide(conquest.giveTerritoryTo);
            if (!receivingSide)
                continue;
            console.log(`[MoveExecutor] Transferring group of ${conquest.positions.length} territories ` +
                `from side ${sideId} to side ${conquest.giveTerritoryTo}`);
            for (const position of conquest.positions) {
                const territory = this.board.getTerritoryAt(position);
                if (!territory || territory.owner !== sideId)
                    continue;
                // Ходовое поле врага = 30 очков и здесь тоже. Считаем ДО loseTerritory(),
                // которая вычищает позицию из turnActiveFields прежнего владельца.
                const wasActiveField = this.board.isActiveFieldOf(sideId, position);
                // Remove from current owner.
                side.loseTerritory(position);
                if (territory.occupiedBy !== null) {
                    territory.vacate();
                }
                // Transfer to recipient — mirror the normal surround-capture path:
                // occupy + addTurnActiveField + setActiveField + capturedOnLastMove.
                territory.capture(conquest.giveTerritoryTo, wasActiveField);
                territory.occupy(conquest.giveTerritoryTo);
                receivingSide.captureTerritory(position);
                receivingSide.addTurnActiveField(position);
                receivingSide.setActiveField(position, true);
                receivingSide.addCapturedOnLastMove(position);
                this.trapResolver.checkAndReanimate(conquest.giveTerritoryTo, position, sideId, ReanimationReason.TRAP_CHOICE);
                lastRecipient = conquest.giveTerritoryTo;
                this.eventBus.publish(new TerritoryCapturedEvent(position, sideId, conquest.giveTerritoryTo, 'surrounding'));
            }
        }
        side.clearPendingGroupConquests();
        // Check eliminations after transfer.
        for (const [sid, s] of this.board.sides) {
            if (s.isActive && s.territories.size === 0) {
                s.eliminate();
                this.eventBus.publish(new SideEliminatedEvent(sid, 'no-territories', sideId, sid === sideId ? lastRecipient : undefined));
            }
        }
    }
    /**
     * End-of-turn group-based conquest resolution
     * Checks all groups on the board and captures those fully surrounded by other players
     * Uses iterative simultaneous resolution: each wave captures only fields reachable by
     * opponents in the current board state, then applies all captures at once before repeating.
     */
    async resolveGroupBasedConquest(result) {
        console.log('[MoveExecutor] Resolving group-based conquest at turn end');
        this.conquestTakers.clear();
        const allGroups = this.groupAnalyzer.findAllGroups();
        const surroundedGroups = allGroups.filter(g => g.isSurrounded && g.owner !== null);
        console.log(`[MoveExecutor] Found ${surroundedGroups.length} surrounded groups`);
        for (const group of surroundedGroups) {
            console.log(`[MoveExecutor] Processing surrounded group owned by side ${group.owner}, ${group.positions.length} positions`);
            await this.resolveGroupIteratively(group.positions, group.owner, result);
        }
        // Check for eliminated sides
        for (let sideId = 0; sideId <= 9; sideId++) {
            const side = this.board.getSide(sideId);
            if (side && side.isActive && side.territories.size === 0) {
                side.eliminate();
                if (!result.eliminatedSides?.includes(sideId)) {
                    result.eliminatedSides?.push(sideId);
                    this.eventBus.publish(new SideEliminatedEvent(sideId, 'no-territories', this.board.currentSide, this.conquestTakers.get(sideId)));
                }
            }
        }
    }
    /**
     * Iterative simultaneous resolution for a captured group.
     *
     * Each wave:
     *   1. Check every remaining field — calculate forces from opponent tiles only
     *      (the trapped player's tiles are excluded, so interior fields surrounded only
     *      by their own group are skipped this wave).
     *   2. Fields with a clear winner are marked for capture.
     *      Fields in parity are NOT burned immediately — they are flagged as
     *      "parity-deferred" so subsequent waves can re-evaluate them (captures
     *      of adjacent fields can shift the force balance and break the parity).
     *   3. Apply all captures simultaneously (ownership changes at once).
     *   4. Repeat until no more captures happen in a wave.
     *
     * After the wave loop terminates, any field still flagged as parity-deferred
     * is genuinely undecidable — only at this point is it burned.
     *
     * This peels the group from the outside in, splitting it between opponents correctly.
     */
    async resolveGroupIteratively(groupPositions, trappedSideId, result) {
        const remaining = new Set(groupPositions.map(p => p.toKey()));
        const parityDeferred = new Set();
        let capturedThisWave = true;
        let wave = 0;
        while (capturedThisWave && remaining.size > 0) {
            capturedThisWave = false;
            wave++;
            // Phase 1: determine outcome for each reachable field (do NOT apply yet)
            const markedCaptures = new Map(); // posKey → winner
            for (const posKey of remaining) {
                const position = Position.fromKey(posKey);
                const territory = this.board.getTerritoryAt(position);
                // If territory was already changed externally, drop it from remaining
                if (!territory || territory.owner !== trappedSideId) {
                    remaining.delete(posKey);
                    parityDeferred.delete(posKey);
                    continue;
                }
                // Calculate forces excluding the trapped player.
                // Fields whose only neighbors are other trapped-player tiles will return
                // forces=[] here and be skipped until those neighbors are claimed by opponents.
                const forceCalculator = new ForceCalculator(this.board);
                const forces = forceCalculator.calculateForces(position, trappedSideId);
                if (forces.length === 0) {
                    // No opponent adjacent yet — interior field, skip this wave
                    continue;
                }
                const winner = forceCalculator.determineWinner(forces);
                if (winner === null) {
                    // Parity — defer the burn decision. Subsequent waves may capture
                    // adjacent fields and shift the force balance for this position.
                    if (!parityDeferred.has(posKey)) {
                        console.log(`[MoveExecutor] Parity at ${posKey} — deferred, will re-check next wave`);
                        parityDeferred.add(posKey);
                    }
                }
                else {
                    markedCaptures.set(posKey, winner);
                    // If this field was previously in parity, it has now resolved.
                    parityDeferred.delete(posKey);
                    capturedThisWave = true;
                }
            }
            // Phase 2: apply all captures simultaneously
            for (const [posKey, winner] of markedCaptures) {
                remaining.delete(posKey);
                const position = Position.fromKey(posKey);
                await this.captureTerritoryForConquest(position, trappedSideId, winner, result, wave);
            }
        }
        // Final phase: any field still flagged as parity (and not captured by a
        // later wave) is genuinely undecidable — burn it now.
        for (const posKey of parityDeferred) {
            if (!remaining.has(posKey))
                continue;
            const position = Position.fromKey(posKey);
            const territory = this.board.getTerritoryAt(position);
            if (!territory || territory.owner !== trappedSideId) {
                remaining.delete(posKey);
                continue;
            }
            console.log(`[MoveExecutor] Parity at ${posKey} still unresolved after all captures — burning`);
            this.burnTerritoryForConquest(position, trappedSideId, result);
            remaining.delete(posKey);
        }
    }
    /**
     * Burns a territory due to parity in end-of-turn conquest
     */
    burnTerritoryForConquest(position, previousOwner, result) {
        const territory = this.board.getTerritoryAt(position);
        if (!territory)
            return;
        console.log(`[MoveExecutor] Burning territory at ${position.toKey()} due to parity`);
        // Remove from previous owner
        const previousOwnerSide = this.board.getSide(previousOwner);
        if (previousOwnerSide) {
            previousOwnerSide.loseTerritory(position);
        }
        // Burn the territory
        territory.burn();
        // Remove piece if occupied
        if (territory.occupiedBy !== null) {
            territory.vacate();
        }
        // Add to result
        if (result.capturedTerritories) {
            result.capturedTerritories.push(position);
        }
        if (result.sideEffects) {
            result.sideEffects.push(`Territory ${position.toKey()} burned due to conquest parity`);
        }
        // End-of-turn conquest parity burn: second (independent) burn path. Emit the
        // scorched-field event so the tutorial hint (#14) fires here too. No
        // TerritoryCapturedEvent — the field becomes BURNED, owned by no one.
        this.eventBus.publish(new TerritoryBurnedEvent(position, 'parity'));
    }
    /**
     * Captures a territory for end-of-turn conquest
     */
    async captureTerritoryForConquest(position, previousOwner, newOwner, result, wave) {
        const territory = this.board.getTerritoryAt(position);
        if (!territory)
            return;
        console.log(`[MoveExecutor] Capturing ${position.toKey()} from side ${previousOwner} to side ${newOwner}`);
        // Check if it was an active field (using activeFields, NOT turnActiveFields)
        // This ensures we only give 30 points for TRULY active fields
        let wasActiveField = false;
        const previousOwnerSide = this.board.getSide(previousOwner);
        if (previousOwnerSide) {
            wasActiveField =
                previousOwnerSide.isActiveField(position) &&
                    territory.owner === previousOwner;
            // Remove from previous owner
            previousOwnerSide.loseTerritory(position);
            this.conquestTakers.set(previousOwner, newOwner);
        }
        // Capture the territory — mirror the normal surround-capture path:
        // occupy + addTurnActiveField + setActiveField + capturedOnLastMove.
        // #D2: флаг через параметр, сбрасывается при перезахвате не-активного поля.
        territory.capture(newOwner, wasActiveField);
        territory.occupy(newOwner);
        // Add to new owner and reanimate if they were eliminated/suspended
        const newOwnerSide = this.board.getSide(newOwner);
        if (newOwnerSide) {
            newOwnerSide.captureTerritory(position);
            newOwnerSide.addTurnActiveField(position);
            newOwnerSide.setActiveField(position, true);
            newOwnerSide.addCapturedOnLastMove(position);
            this.trapResolver.checkAndReanimate(newOwner, position, previousOwner, ReanimationReason.TRAP_CAPTURE);
        }
        // Add to result
        if (result.capturedTerritories) {
            result.capturedTerritories.push(position);
        }
        if (result.sideEffects) {
            result.sideEffects.push(`Territory ${position.toKey()} conquered by side ${newOwner}`);
        }
        // Emit event
        this.eventBus.publish(new TerritoryCapturedEvent(position, previousOwner, newOwner, 'end-turn-conquest', undefined, undefined, undefined, wave));
    }
}
//# sourceMappingURL=MoveExecutor.js.map